import type { EtatSecondDiagnostic } from "../lib/api";

/**
 * Ce qu'on dit à une session qui revient avant terme — 31/08/2026.
 *
 * Le parrainage ouvre le second diagnostic à quatre-vingt-dix jours. Tant que
 * rien ne fermait ce second diagnostic, cette phrase promettait un accès que
 * personne n'avait perdu ; la limite existe désormais, et il faut la dire.
 *
 * **Trois règles tiennent cette copie.**
 *
 * 1. **On rend, on ne refuse pas.** La première chose affichée est le résultat
 *    précédent — le produit se souvient. Une porte fermée qui ne montre rien
 *    donne l'impression d'une panne, et c'en serait une du point de vue du
 *    visiteur.
 * 2. **Deux chemins, toujours les deux.** Attendre, ou faire passer le test à
 *    deux personnes. N'en donner qu'un ferait de la limite une contrainte au
 *    lieu d'une contrepartie.
 * 3. **Aucun chiffre qui ne vienne du serveur.** La date de réouverture, le
 *    score, le métier : tout est rendu par `/diagnostic`. Rien n'est recalculé
 *    ici, où l'on pourrait se tromper d'un jour sans que personne ne le voie.
 */

/** Le jour d'une date ISO, en français, sans l'heure — qui n'apprendrait rien. */
export function jour(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * La phrase complète, construite à partir de ce que le serveur a rendu.
 *
 * Rend `null` quand l'état ne ferme rien : l'appelant n'a alors rien à dire, et
 * fabriquer une phrase à partir d'un état ouvert serait la meilleure façon
 * d'afficher un refus qui n'existe pas.
 */
export function messageSecondDiagnostic(etat: EtatSecondDiagnostic): string | null {
  if (etat.ouvert) return null;

  const rappel =
    etat.precedent && etat.dernierLe
      ? etat.precedent.mode === "metier" && etat.precedent.metierNormalise !== ""
        ? `Vous avez mesuré ${etat.precedent.metierNormalise} le ${jour(etat.dernierLe)} : indice ${etat.precedent.score} sur 100.`
        : `Vous avez fait ce diagnostic le ${jour(etat.dernierLe)} : indice ${etat.precedent.score} sur 100.`
      : "Vous avez déjà fait ce diagnostic.";

  const quand = etat.rouvreLe
    ? `Le prochain s'ouvre le ${jour(etat.rouvreLe)}`
    : "Le prochain s'ouvrira plus tard";

  /*
    « Refaire le test avant d'avoir agi ne mesure rien » n'est pas une excuse
    trouvée après coup : c'est déjà ce que le rapport dit en programmant son
    rendez-vous à quatre-vingt-dix jours. La limite rend ce conseil littéral.
  */
  return (
    `${rappel} Refaire le test avant d'avoir changé quelque chose ne mesurerait rien de neuf — ` +
    `c'est pourquoi le rapport vous donne rendez-vous à quatre-vingt-dix jours. ` +
    `${quand}. Il s'ouvre tout de suite si deux personnes passent le test depuis votre lien de partage.`
  );
}
